import { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Users, Plus, Search, Pencil, Trash2, Loader2, UserRound, ScanLine } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { GENDERS, RELIGIONS, MARITAL, BLOOD, EDUCATION, RELATIONSHIP, CITIZENSHIP, OCCUPATION } from "@/constants/refData";

const EMPTY = {
  nik: "", full_name: "", kk_number: "", gender: "", birth_place: "", birth_date: "",
  dusun: "", rt: "", rw: "", religion: "", education: "", marital_status: "", marriage_date: "",
  occupation: "", blood_type: "", relationship: "", citizenship: "WNI", passport_no: "",
  kitap_no: "", father_name: "", mother_name: "", address: "",
};

export default function Residents() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("person"); // "person" | "kk"
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [units, setUnits] = useState([]);

  useEffect(() => {
    api.get("/village-units").then((r) => setUnits(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async (s = "") => {
    setLoading(true);
    try {
      const r = await api.get("/residents", { params: s ? { search: s } : {} });
      setItems(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(search), 400);
    return () => clearTimeout(t);
  }, [search, load]);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setDialog(true); };

  // Group residents by Kartu Keluarga for the "Per KK" view.
  const kkGroups = useMemo(() => {
    const map = new Map();
    for (const r of items) {
      const key = (r.kk_number || "").trim() || "__none__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    const groups = Array.from(map.entries()).map(([key, members]) => {
      // Urutan anggota: Kepala Keluarga (0) -> Istri/Suami (1) -> lainnya (2) urut tanggal lahir (tertua dulu).
      const relRank = (rel) => {
        const s = (rel || "").toLowerCase();
        if (/kepala/.test(s)) return 0;
        if (/istri|suami/.test(s)) return 1;
        return 2;
      };
      const parseDMY = (d) => {
        const m = (d || "").match(/(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})/);
        if (!m) return Infinity;
        const yy = m[3].length === 2 ? 2000 + parseInt(m[3], 10) : parseInt(m[3], 10);
        const t = new Date(yy, parseInt(m[2], 10) - 1, parseInt(m[1], 10)).getTime();
        return Number.isNaN(t) ? Infinity : t;
      };
      const sorted = [...members].sort((a, b) => {
        const ra = relRank(a.relationship), rb = relRank(b.relationship);
        if (ra !== rb) return ra - rb;
        const da = parseDMY(a.birth_date), dbb = parseDMY(b.birth_date);
        if (da !== dbb) return da - dbb;
        return (a.full_name || "").localeCompare(b.full_name || "");
      });
      const head = sorted.find((m) => /kepala/i.test(m.relationship || "")) || sorted[0];
      return { key, kk_number: key === "__none__" ? "" : key, members: sorted, head };
    });
    groups.sort((a, b) => {
      if (!a.kk_number) return 1;
      if (!b.kk_number) return -1;
      return a.kk_number.localeCompare(b.kk_number);
    });
    return groups;
  }, [items]);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{16}$/.test(form.nik)) { toast.error("NIK harus 16 digit angka"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/residents/${editing.id}`, form);
        toast.success("Data warga diperbarui");
      } else {
        await api.post("/residents", form);
        toast.success("Warga baru ditambahkan");
      }
      setDialog(false);
      load(search);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/residents/${toDelete.id}`);
      toast.success("Warga dihapus");
      setToDelete(null);
      load(search);
    } catch (e) { toast.error(apiError(e)); }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Cascading dusun/RW/RT options from registered village units
  const withCurrent = (opts, cur) => (cur && !opts.includes(cur) ? [cur, ...opts] : opts);
  const dusunOpts = units.map((d) => d.name);
  const selDusun = units.find((d) => d.name === form.dusun);
  const rwOpts = (selDusun?.children || []).map((rw) => rw.name);
  const selRw = (selDusun?.children || []).find((rw) => rw.name === form.rw);
  const rtOpts = (selRw?.children || []).map((rt) => rt.name);
  const setDusun = (v) => setForm((f) => ({ ...f, dusun: v, rw: "", rt: "" }));
  const setRw = (v) => setForm((f) => ({ ...f, rw: v, rt: "" }));

  // Marriage date only applies when the person is / was married.
  const notMarried = /belum\s*kawin/i.test(form.marital_status || "") || !String(form.marital_status || "").trim();
  const setMarital = (v) =>
    setForm((f) => ({ ...f, marital_status: v, marriage_date: /belum\s*kawin/i.test(v) ? "" : f.marriage_date }));

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader
        icon={Users} title="Data Warga" subtitle="Kelola data penduduk desa (Kartu Keluarga & KTP)"
        action={
          <div className="flex gap-2">
            <Button onClick={() => navigate("/kartu-keluarga")} variant="outline" data-testid="resident-scan-kk-button" className="rounded-xl h-11 border-[#064E3B] text-[#064E3B] hover:bg-emerald-50">
              <ScanLine size={18} className="mr-1.5" /> Pindai KK
            </Button>
            <Button onClick={openAdd} data-testid="resident-add-button" className="bg-[#064E3B] hover:bg-[#043E2F] rounded-xl h-11">
              <Plus size={18} className="mr-1.5" /> Tambah Warga
            </Button>
          </div>
        }
      />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 sm:max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Cari nama, NIK, atau No. KK..." value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="resident-search-input" className="pl-10 h-11 bg-white"
          />
        </div>
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 self-start" data-testid="resident-view-toggle">
          <button
            type="button" onClick={() => setViewMode("person")} data-testid="resident-view-person"
            className={`flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-medium transition ${viewMode === "person" ? "bg-[#064E3B] text-white" : "text-slate-600 hover:bg-slate-50"}`}
          >
            <UserRound size={16} /> Per Orang
          </button>
          <button
            type="button" onClick={() => setViewMode("kk")} data-testid="resident-view-kk"
            className={`flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-medium transition ${viewMode === "kk" ? "bg-[#064E3B] text-white" : "text-slate-600 hover:bg-slate-50"}`}
          >
            <Users size={16} /> Per KK
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 grid place-items-center"><Loader2 className="animate-spin text-[#064E3B]" size={28} /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-emerald-50 grid place-items-center mx-auto mb-4"><UserRound className="text-[#064E3B]" /></div>
          <p className="text-slate-500">{`Belum ada data warga. Klik "Tambah Warga" untuk memulai.`}</p>
        </div>
      ) : viewMode === "person" ? (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto custom-scroll">
            <table className="w-full text-sm" data-testid="resident-table">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 font-semibold">NIK</th>
                  <th className="px-4 py-3 font-semibold">Nama Lengkap</th>
                  <th className="px-4 py-3 font-semibold">No. KK</th>
                  <th className="px-4 py-3 font-semibold">L/P</th>
                  <th className="px-4 py-3 font-semibold">Dusun / RT / RW</th>
                  <th className="px-4 py-3 font-semibold">Pekerjaan</th>
                  <th className="px-4 py-3 font-semibold text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`resident-row-${r.id}`}>
                    <td className="px-4 py-3 font-mono text-slate-700">{r.nik}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{r.full_name}</td>
                    <td className="px-4 py-3 font-mono text-slate-600">{r.kk_number || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.gender || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{[r.dusun, r.rt && `RT ${r.rt}`, r.rw && `RW ${r.rw}`].filter(Boolean).join(" / ") || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.occupation || "-"}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(r)} data-testid={`resident-edit-${r.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-[#064E3B]"><Pencil size={16} /></button>
                        <button onClick={() => setToDelete(r)} data-testid={`resident-delete-${r.id}`} className="p-2 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="resident-kk-groups">
          {kkGroups.map((g) => (
            <div key={g.key} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`kk-group-${g.key}`}>
              <div className="flex items-center justify-between gap-3 px-4 py-3 bg-emerald-50/60 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-xl bg-[#064E3B]/10 grid place-items-center shrink-0"><Users size={18} className="text-[#064E3B]" /></div>
                  <div>
                    <p className="font-semibold text-slate-900 font-mono">{g.kk_number || "Tanpa No. KK"}</p>
                    <p className="text-xs text-slate-500">Kepala Keluarga: <span className="font-medium text-slate-700">{g.head?.full_name || "-"}</span></p>
                  </div>
                </div>
                <span className="text-xs font-medium text-[#064E3B] bg-white border border-emerald-200 rounded-full px-3 py-1 shrink-0">{g.members.length} anggota</span>
              </div>
              <div className="overflow-x-auto custom-scroll">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                      <th className="px-4 py-2.5 font-semibold">NIK</th>
                      <th className="px-4 py-2.5 font-semibold">Nama Lengkap</th>
                      <th className="px-4 py-2.5 font-semibold">Hubungan</th>
                      <th className="px-4 py-2.5 font-semibold">L/P</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.members.map((r) => (
                      <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`resident-row-${r.id}`}>
                        <td className="px-4 py-2.5 font-mono text-slate-700">{r.nik}</td>
                        <td className="px-4 py-2.5 font-medium text-slate-900">{r.full_name}</td>
                        <td className="px-4 py-2.5 text-slate-600">{r.relationship || "-"}</td>
                        <td className="px-4 py-2.5 text-slate-600">{r.gender || "-"}</td>
                        <td className="px-4 py-2.5">
                          <div className="flex justify-end gap-1">
                            <button onClick={() => openEdit(r)} data-testid={`resident-edit-${r.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-[#064E3B]"><Pencil size={16} /></button>
                            <button onClick={() => setToDelete(r)} data-testid={`resident-delete-${r.id}`} className="p-2 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600"><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto custom-scroll" data-testid="resident-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? "Ubah Data Warga" : "Tambah Warga Baru"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 py-2">
            <F label="NIK (16 digit)" value={form.nik} onChange={set("nik")} testid="resident-nik-input" required />
            <F label="Nama Lengkap" value={form.full_name} onChange={set("full_name")} testid="resident-name-input" required />
            <F label="No. Kartu Keluarga" value={form.kk_number} onChange={set("kk_number")} testid="resident-kk-input" />
            <SelField label="Jenis Kelamin" value={form.gender} onChange={set("gender")} options={GENDERS} testid="resident-gender-select" />
            <F label="Tempat Lahir" value={form.birth_place} onChange={set("birth_place")} testid="resident-birthplace-input" />
            <DateField label="Tanggal Lahir" value={form.birth_date} onChange={set("birth_date")} testid="resident-birthdate-input" />
            {units.length > 0 ? (
              <>
                <SelField label="Dusun" value={form.dusun} onChange={setDusun} options={withCurrent(dusunOpts, form.dusun)} testid="resident-dusun-select" />
                <div className="grid grid-cols-2 gap-3">
                  <SelField label="RW" value={form.rw} onChange={setRw} options={withCurrent(rwOpts, form.rw)} testid="resident-rw-select" />
                  <SelField label="RT" value={form.rt} onChange={set("rt")} options={withCurrent(rtOpts, form.rt)} testid="resident-rt-select" />
                </div>
              </>
            ) : (
              <>
                <F label="Dusun" value={form.dusun} onChange={set("dusun")} testid="resident-dusun-input" />
                <div className="grid grid-cols-2 gap-3">
                  <F label="RT" value={form.rt} onChange={set("rt")} testid="resident-rt-input" />
                  <F label="RW" value={form.rw} onChange={set("rw")} testid="resident-rw-input" />
                </div>
              </>
            )}
            <SelField label="Agama" value={form.religion} onChange={set("religion")} options={RELIGIONS} testid="resident-religion-select" />
            <SelField label="Pendidikan" value={form.education} onChange={set("education")} options={EDUCATION} testid="resident-education-select" />
            <SelField label="Status Perkawinan" value={form.marital_status} onChange={setMarital} options={MARITAL} testid="resident-marital-select" />
            {notMarried ? (
              <div className="hidden sm:block" aria-hidden="true" />
            ) : (
              <DateField label="Tanggal Perkawinan" value={form.marriage_date} onChange={set("marriage_date")} testid="resident-marriagedate-input" />
            )}
            <SelField label="Pekerjaan" value={form.occupation} onChange={set("occupation")} options={OCCUPATION} testid="resident-occupation-select" />
            <SelField label="Golongan Darah" value={form.blood_type} onChange={set("blood_type")} options={BLOOD} testid="resident-blood-select" />            <SelField label="Status Hubungan Keluarga" value={form.relationship} onChange={set("relationship")} options={RELATIONSHIP} testid="resident-relationship-select" />
            <SelField label="Kewarganegaraan" value={form.citizenship} onChange={set("citizenship")} options={CITIZENSHIP} testid="resident-citizenship-select" />
            <F label="No. Paspor" value={form.passport_no} onChange={set("passport_no")} testid="resident-passport-input" />
            <F label="No. KITAP" value={form.kitap_no} onChange={set("kitap_no")} testid="resident-kitap-input" />
            <F label="Nama Ayah" value={form.father_name} onChange={set("father_name")} testid="resident-father-input" />
            <F label="Nama Ibu" value={form.mother_name} onChange={set("mother_name")} testid="resident-mother-input" />
            <div className="sm:col-span-2">
              <F label="Alamat Lengkap" value={form.address} onChange={set("address")} testid="resident-address-input" />
            </div>
            <DialogFooter className="sm:col-span-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setDialog(false)} className="rounded-xl">Batal</Button>
              <Button type="submit" disabled={saving} data-testid="resident-save-button" className="bg-[#064E3B] hover:bg-[#043E2F] rounded-xl">
                {saving && <Loader2 className="animate-spin mr-2" size={16} />} Simpan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="resident-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus data warga?</AlertDialogTitle>
            <AlertDialogDescription>Data <b>{toDelete?.full_name}</b> akan dihapus permanen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="resident-confirm-delete" className="bg-red-600 hover:bg-red-700 rounded-xl">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function PageHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-2xl bg-emerald-50 grid place-items-center"><Icon className="text-[#064E3B]" size={24} /></div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500">{subtitle}</p>
        </div>
      </div>
      {action}
    </motion.div>
  );
}

function F({ label, value, onChange, testid, type = "text", required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}{required && <span className="text-red-500"> *</span>}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-11" required={required} />
    </div>
  );
}

// Convert stored DD-MM-YYYY (KK format) to ISO YYYY-MM-DD for the native date picker.
function toISO(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value; // already ISO
  const m = String(value).match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/); // DD-MM-YYYY
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return "";
}
// Convert picker's ISO YYYY-MM-DD back to DD-MM-YYYY for storage (matches KK).
function fromISO(iso) {
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : iso;
}

function DateField({ label, value, onChange, testid }) {
  const iso = toISO(value);
  // If a value exists but can't be parsed to a real date, show it as editable text
  // so nothing is hidden — the user can correct it to DD-MM-YYYY.
  if (value && !iso) {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm">{label}</Label>
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="DD-MM-YYYY" data-testid={testid} className="h-11" />
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <Input
        type="date"
        value={iso}
        onChange={(e) => onChange(e.target.value ? fromISO(e.target.value) : "")}
        data-testid={testid}
        className="h-11"
      />
    </div>
  );
}

function SelField({ label, value, onChange, options, testid }) {
  // Always include the stored value as an option so data saved from a KK scan
  // (often UPPERCASE, e.g. "BELUM KAWIN", "ISLAM") is never hidden by the dropdown.
  const merged = value && !options.includes(value) ? [value, ...options] : options;
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="h-11" data-testid={testid}><SelectValue placeholder="Pilih..." /></SelectTrigger>
        <SelectContent>
          {merged.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
